import {
    CONDITION_ERROR,
    CONDITION_ERROR_MESSAGE,
    CONDITION_GROUP_OPERATOR,
    CONDITION_OPERATORS,
    IConditionByLevelPreset,
    IConditionError,
    IConditionFieldPreset,
    IConditionPresetGroup,
    POLICY_CONDITION_COMPARE_VALUE_TYPE,
    ICompareValueItem
} from "../../../../../policy-types"
import { IDateCompareValue } from "../../../../../stores/compare-value/date-compare-value-store";
import { INumberBetweenCompareValue } from "../../../../../stores/compare-value/integer-between-compare-value-store";
import { IKeyValueCompareValue } from "../../../../../stores/compare-value/key-value-compare-value-store";
import LocationSemanticGroupConverter from "../../../../../stores/compare-value/location-compare-value-list-stores/location-semantic-group-converter";
import { ILocationCompareValueList, ILocationToken } from "../../../../../stores/compare-value/location-compare-value-list-stores/location-selection-types";
import { IStringCompareValue } from "../../../../../stores/compare-value/purchased-compare-value-store";
import { IValueTypeCompareValue } from "../../../../../stores/compare-value/window-compare-value-store";
import { IPolicyConditionChanges } from "../../../../../stores/condition-builder-store";
import getIntersections from "./helpers/math-ranges-instersections";
import { isInteger, toNumber, toString, isString, isNumber } from "./helpers/type-convertions";


export interface IConditionValidatorData extends IPolicyConditionChanges {
    compare_value: any;
    error?: IConditionError
}

export default class ConditionValidator {
    private condition: IConditionValidatorData;
    private otherConditions: IConditionValidatorData[];
    private groupPreset: IConditionPresetGroup;
    private groupOp: CONDITION_GROUP_OPERATOR;
    private conditionPreset: {
        [key: string]: IConditionFieldPreset
    };
    private semanticGroupConverter?: LocationSemanticGroupConverter;

    constructor(condition: IPolicyConditionChanges, otherConditions: IPolicyConditionChanges[], preset: IConditionByLevelPreset, groupOp: CONDITION_GROUP_OPERATOR) {
        this.condition = condition as IConditionValidatorData;
        this.otherConditions = otherConditions as IConditionValidatorData[];
        this.groupPreset = preset.group;
        this.conditionPreset = preset.conditions;
        this.groupOp = groupOp;
    }

    public validate(): { conditionResponse: IConditionValidatorData, otherConditionsResponse: IConditionValidatorData[] } {

        /** ensure valid currentItem fields values and types */
        let isValid = this.isValidCondition();

        /** check for duplicates */
        if (isValid) {
            isValid = !this.hasDuplicates();
        }

        /** check contradicting conditions only for condition with AND between them */
        if (isValid && this.groupOp === CONDITION_GROUP_OPERATOR.OP_AND) {
            isValid = this.hasContradictions();
        }

        return { conditionResponse: this.condition, otherConditionsResponse: this.otherConditions };
    }

    private isValidCondition(): boolean {
        const config = this.conditionPreset[this.condition.field as string];
        /** ensure field */
        if (!config) {
            this.setError(this.condition, CONDITION_ERROR.INCOMPLETE, CONDITION_ERROR_MESSAGE.INCOMPLETE);
            return false;
        }
        /** ensure operator */
        if (!config.operators.find(op => op.value === this.condition.operator)) {
            this.setError(this.condition, CONDITION_ERROR.INCOMPLETE, CONDITION_ERROR_MESSAGE.INCOMPLETE);
            return false;
        }

        /** compre value exists */
        if (this.condition.compare_value === null || this.condition.compare_value === undefined) {
            this.setError(this.condition, CONDITION_ERROR.INCOMPLETE, CONDITION_ERROR_MESSAGE.INCOMPLETE);
            return false;
        }

        /** ensure compare_value is correct */
        switch (config.compare_value_type) {
            case POLICY_CONDITION_COMPARE_VALUE_TYPE.DATE: {
                const { from, to } = this.condition.compare_value as IDateCompareValue;

                /** ISO 8601 format regex: YYYY-MM-DDTHH:MM:SS.SSSZ or YYYY-MM-DDTHH:MM:SSZ */
                const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z$/;

                /** Validate from date exists and is in ISO format */
                if (!from || !isoDateRegex.test(from)) {
                    this.setError(this.condition, CONDITION_ERROR.INCOMPLETE, CONDITION_ERROR_MESSAGE.INCOMPLETE);
                    return false;
                }

                /** Validate to date is in ISO format if present */
                if (to && !isoDateRegex.test(to)) {
                    this.setError(this.condition, CONDITION_ERROR.INCOMPLETE, CONDITION_ERROR_MESSAGE.INCOMPLETE);
                    return false;
                }

                /** For OP_BETWEEN operator, both from and to are required */
                if (this.condition.operator === CONDITION_OPERATORS.OP_BETWEEN && !to) {
                    this.setError(this.condition, CONDITION_ERROR.INCOMPLETE, CONDITION_ERROR_MESSAGE.INCOMPLETE);
                    return false;
                }

                /** For OP_ON operator, to must also exist (DatePickerV2 always returns both from and to) */
                if (this.condition.operator === CONDITION_OPERATORS.OP_ON && !to) {
                    this.setError(this.condition, CONDITION_ERROR.INCOMPLETE, CONDITION_ERROR_MESSAGE.INCOMPLETE);
                    return false;
                }
                break;
            }

            case POLICY_CONDITION_COMPARE_VALUE_TYPE.INTEGER_BETWEEN: {
                const int_between_data = this.condition.compare_value as INumberBetweenCompareValue;
                const int_between_op = this.condition.operator;

                if (!int_between_data || !int_between_data.from) {
                    this.setError(this.condition, CONDITION_ERROR.INCOMPLETE, CONDITION_ERROR_MESSAGE.INCOMPLETE);
                    return false;
                }

                const val_from = toNumber(int_between_data.from);
                if (val_from === undefined) {
                    this.setError(this.condition, CONDITION_ERROR.INCOMPLETE, CONDITION_ERROR_MESSAGE.INCOMPLETE);
                    return false;
                }

                if (!Number.isInteger((val_from))) {
                    this.setError(this.condition, CONDITION_ERROR.INCOMPLETE, CONDITION_ERROR_MESSAGE.INCOMPLETE);
                    return false;
                }
                if (val_from < 0) {
                    this.setError(this.condition, CONDITION_ERROR.INCOMPLETE, CONDITION_ERROR_MESSAGE.INCOMPLETE);
                    return false;
                }

                if (int_between_op === CONDITION_OPERATORS.OP_BETWEEN_INTEGER) {
                    if (!int_between_data.to) {
                        this.setError(this.condition, CONDITION_ERROR.INCOMPLETE, CONDITION_ERROR_MESSAGE.INCOMPLETE);
                        return false;
                    }
                    const val_to = toNumber(int_between_data.to);
                    if (val_to === undefined) {
                        this.setError(this.condition, CONDITION_ERROR.INCOMPLETE, CONDITION_ERROR_MESSAGE.INCOMPLETE);
                        return false;
                    }

                    if (!isInteger(val_to)) {
                        this.setError(this.condition, CONDITION_ERROR.INCOMPLETE, CONDITION_ERROR_MESSAGE.INCOMPLETE);
                        return false;
                    }
                    if (val_to < 0 || val_to <= val_from) {
                        this.setError(this.condition, CONDITION_ERROR.INCOMPLETE, CONDITION_ERROR_MESSAGE.INCOMPLETE);
                        return false;
                    }
                }
                break;
            }

            case POLICY_CONDITION_COMPARE_VALUE_TYPE.TAG:
            case POLICY_CONDITION_COMPARE_VALUE_TYPE.STRING:
            case POLICY_CONDITION_COMPARE_VALUE_TYPE.CUSTOMER_STATE:
            case POLICY_CONDITION_COMPARE_VALUE_TYPE.PURCHASED: {
                const val = toString(this.condition.compare_value as IStringCompareValue);
                if (!isString(val) || val?.trim() === '') {
                    this.setError(this.condition, CONDITION_ERROR.INCOMPLETE, CONDITION_ERROR_MESSAGE.INCOMPLETE);
                    return false;
                }
                break;
            }

            case POLICY_CONDITION_COMPARE_VALUE_TYPE.RETURN_REASON:
            case POLICY_CONDITION_COMPARE_VALUE_TYPE.INTEGER:
            case POLICY_CONDITION_COMPARE_VALUE_TYPE.PRICE:
            case POLICY_CONDITION_COMPARE_VALUE_TYPE.FLOAT: {
                if (!isNumber(this.condition.compare_value)) {
                    const val = toNumber(this.condition.compare_value);
                    if (!isNumber(val)) {
                        this.setError(this.condition, CONDITION_ERROR.INCOMPLETE, CONDITION_ERROR_MESSAGE.INCOMPLETE);
                        return false;
                    }
                    if (config.compare_value_type === POLICY_CONDITION_COMPARE_VALUE_TYPE.INTEGER && !isInteger(val)) {
                        this.setError(this.condition, CONDITION_ERROR.INCOMPLETE, CONDITION_ERROR_MESSAGE.INCOMPLETE);
                        return false;
                    }
                    if (val as number < 0) {
                        this.setError(this.condition, CONDITION_ERROR.INCOMPLETE, CONDITION_ERROR_MESSAGE.INCOMPLETE);
                        return false;
                    }
                    this.condition.compare_value = val;
                }
                break;
            }

            case POLICY_CONDITION_COMPARE_VALUE_TYPE.WINDOW:
            case POLICY_CONDITION_COMPARE_VALUE_TYPE.WEIGHT: {
                const compVal = this.condition.compare_value as IValueTypeCompareValue;
                if (!isNumber(compVal.value)) {
                    const val = toNumber(this.condition.compare_value.value);
                    if (!isNumber(val)) {
                        this.setError(this.condition, CONDITION_ERROR.INCOMPLETE, CONDITION_ERROR_MESSAGE.INCOMPLETE);
                        return false;
                    }
                    if (config.compare_value_type === POLICY_CONDITION_COMPARE_VALUE_TYPE.WINDOW && !isInteger(val)) {
                        this.setError(this.condition, CONDITION_ERROR.INCOMPLETE, CONDITION_ERROR_MESSAGE.INCOMPLETE);
                        return false;
                    }
                    if (val as number < 0) {
                        this.setError(this.condition, CONDITION_ERROR.INCOMPLETE, CONDITION_ERROR_MESSAGE.INCOMPLETE);
                        return false;
                    }
                }
                if (!isString(compVal.type) || compVal.type.trim() === '') {
                    this.setError(this.condition, CONDITION_ERROR.INCOMPLETE, CONDITION_ERROR_MESSAGE.INCOMPLETE);
                    return false;
                }
                break;
            }

            case POLICY_CONDITION_COMPARE_VALUE_TYPE.LOCATION: {
                const compareValueRaw: any = this.condition.compare_value;

                /** New list-based format: { _ver: 2, list?: [ { country, region?, city? }, ... ] }. */
                if (compareValueRaw && typeof compareValueRaw === 'object' && (compareValueRaw as ILocationCompareValueList)._ver === 2) {
                    const locationTokensList = compareValueRaw.list;

                    /** List is undefined - no selection made */
                    if (!locationTokensList) {
                        this.setError(this.condition, CONDITION_ERROR.INCOMPLETE, CONDITION_ERROR_MESSAGE.INCOMPLETE);
                        return false;
                    }

                    /** List is empty array - "select all" state is not allowed for saving */
                    if (!locationTokensList.length) {
                        this.setError(this.condition, CONDITION_ERROR.INCOMPLETE, CONDITION_ERROR_MESSAGE.INCOMPLETE);
                        return false;
                    }

                    const semanticGroupConverter = this.getSemanticGroupConverter();

                    /** Group context is checked before expansion, which would drop or rewrite invalid metadata */
                    for (const token of locationTokensList as ILocationToken[]) {
                        if (!semanticGroupConverter.isTokenGroupContextValid(token)) {
                            this.setError(this.condition, CONDITION_ERROR.INCOMPLETE, CONDITION_ERROR_MESSAGE.INCOMPLETE);
                            return false;
                        }
                    }

                    const persistenceTokens = semanticGroupConverter.expandTokensForPersistence(locationTokensList);

                    /** UI-only group tokens must expand to member countries before save */
                    if (!persistenceTokens.length) {
                        this.setError(this.condition, CONDITION_ERROR.INCOMPLETE, CONDITION_ERROR_MESSAGE.INCOMPLETE);
                        return false;
                    }

                    const isLettersOnly = (val: string): boolean => /^[A-Za-z]+$/.test(val);

                    for (const token of persistenceTokens) {
                        const countryCode = token?.country;
                        if (!isString(countryCode) || !isLettersOnly(countryCode) || countryCode.length !== 2) {
                            this.setError(this.condition, CONDITION_ERROR.INCOMPLETE, CONDITION_ERROR_MESSAGE.INCOMPLETE);
                            return false;
                        }

                        if (semanticGroupConverter.isSemanticGroupCode(countryCode)) {
                            this.setError(this.condition, CONDITION_ERROR.INCOMPLETE, CONDITION_ERROR_MESSAGE.INCOMPLETE);
                            return false;
                        }
                    }

                    break;
                }
                break;
            }

            case POLICY_CONDITION_COMPARE_VALUE_TYPE.KEY_VALUE: {
                const compVal = this.condition.compare_value as IKeyValueCompareValue;
                /** check the key, required */
                if (!isString(compVal.key) || compVal.key.trim() === '') {
                    this.setError(this.condition, CONDITION_ERROR.INCOMPLETE, CONDITION_ERROR_MESSAGE.INCOMPLETE);
                    return false;
                }
                /** check the value according to op */
                /** if not requies value and values exists - error */
                if ([CONDITION_OPERATORS.OP_IS_EMPTY, CONDITION_OPERATORS.OP_HAS_ANY_VALUE].includes(this.condition.operator as CONDITION_OPERATORS)) {
                    if (!isString(compVal.value) || compVal.value.trim() !== '') {
                        this.setError(this.condition, CONDITION_ERROR.INCOMPLETE, CONDITION_ERROR_MESSAGE.INCOMPLETE);
                        return false;
                    }
                }
                /** if requires value and it does not exists - error */
                else {
                    if (!isString(compVal.value) || compVal.value.trim() === '') {
                        this.setError(this.condition, CONDITION_ERROR.INCOMPLETE, CONDITION_ERROR_MESSAGE.INCOMPLETE);
                        return false;
                    }
                }
                break;
            }

            default: {
                console.error('[rules-condition-validator.ensureValidCondition] invalid compare_value type ' + config.compare_value_type);
                this.setError(this.condition, CONDITION_ERROR.INCOMPLETE, CONDITION_ERROR_MESSAGE.INCOMPLETE);
                return false;
            }
        }
        return true;
    }

    private hasContradictions(): boolean {
        const config = this.conditionPreset[this.condition.field as string];

        switch (config.compare_value_type) {

            case POLICY_CONDITION_COMPARE_VALUE_TYPE.INTEGER:
            case POLICY_CONDITION_COMPARE_VALUE_TYPE.PRICE:
            case POLICY_CONDITION_COMPARE_VALUE_TYPE.FLOAT:
                return this.hasNumericContradictions();

            case POLICY_CONDITION_COMPARE_VALUE_TYPE.TAG:
            case POLICY_CONDITION_COMPARE_VALUE_TYPE.PURCHASED:
                return false;

            case POLICY_CONDITION_COMPARE_VALUE_TYPE.RETURN_REASON:
            case POLICY_CONDITION_COMPARE_VALUE_TYPE.CUSTOMER_STATE:
                return this.hasReturnReasonContradictions();

            case POLICY_CONDITION_COMPARE_VALUE_TYPE.LOCATION:
                return false;

            case POLICY_CONDITION_COMPARE_VALUE_TYPE.WINDOW:
            case POLICY_CONDITION_COMPARE_VALUE_TYPE.WEIGHT:
                return false;

            case POLICY_CONDITION_COMPARE_VALUE_TYPE.DATE:
            case POLICY_CONDITION_COMPARE_VALUE_TYPE.INTEGER_BETWEEN:
                return false;

            case POLICY_CONDITION_COMPARE_VALUE_TYPE.STRING:
            case POLICY_CONDITION_COMPARE_VALUE_TYPE.KEY_VALUE:
                return false;

            default:
                throw new Error('[rules-condition-validator.hasContradictions] invalid compareValueType ' + config.compare_value_type);
        }
    }

    private hasNumericContradictions(): boolean {
        const allConditions = [this.condition, ...this.otherConditions];
        const intersections = getIntersections(allConditions);

        if (!intersections.length) {
            allConditions.forEach(condition => {
                this.setError(condition, CONDITION_ERROR.CONTRADICT, CONDITION_ERROR_MESSAGE.CONTRADICT);
            });
            return true;
        }

        return false;
    }

    private setError(condition: IConditionValidatorData, type: CONDITION_ERROR, body: CONDITION_ERROR_MESSAGE): void {
        condition.error = { type, body };
    }


    private hasReturnReasonContradictions(): boolean {
        const intersections = this.otherConditions.filter(c => (
            this.condition.operator !== c.operator 
            && this.condition.field === c.field
            && this.areEqualCompareValues(this.condition.compare_value, c.compare_value)
        ));

        if (intersections.length) {
            intersections.forEach(c => {
                this.setError(c, CONDITION_ERROR.CONTRADICT, CONDITION_ERROR_MESSAGE.CONTRADICT)
            });
            this.setError(this.condition, CONDITION_ERROR.CONTRADICT, CONDITION_ERROR_MESSAGE.CONTRADICT);
            return true;
        }

        return false;
    }

    private hasDuplicates(): boolean {
        const duplicates = this.otherConditions.filter(c => (
            this.condition.operator === c.operator && this.condition.field === c.field
            && this.areEqualCompareValues(this.condition.compare_value, c.compare_value)
        ));
        if (duplicates.length > 0) {
            duplicates.forEach(c => {
                this.setError(c, CONDITION_ERROR.DUPLICATED, CONDITION_ERROR_MESSAGE.DUPLICATED);
            });
            this.setError(this.condition, CONDITION_ERROR.DUPLICATED, CONDITION_ERROR_MESSAGE.DUPLICATED);
            return true;
        }
        return false;
    }


    /** comopare value can be object - hence we compare it after stringifying */
    private areEqualCompareValues(compare_value_1: any, compare_value_2: any): boolean {
        const config = this.conditionPreset[this.condition.field as string];

        if (config?.compare_value_type === POLICY_CONDITION_COMPARE_VALUE_TYPE.LOCATION) {
            return this.areEqualLocationCompareValues(compare_value_1, compare_value_2);
        }

        return this.areEqualJsonValues(compare_value_1, compare_value_2);
    }

    private areEqualJsonValues(compare_value_1: any, compare_value_2: any): boolean {
        const nullReplacer = (k: any, v: any) => {
            if (v === null) {
                return undefined;
            }
            return v;
        }

        return JSON.stringify(compare_value_1, nullReplacer) === JSON.stringify(compare_value_2, nullReplacer);
    }

    /**
     * Locations are compared in persistence shape, so that a group token, its member countries,
     * and the same countries in another order all describe the same selection.
     */
    private areEqualLocationCompareValues(compare_value_1: any, compare_value_2: any): boolean {
        if (!this.isLocationList(compare_value_1) || !this.isLocationList(compare_value_2)) {
            return this.areEqualJsonValues(compare_value_1, compare_value_2);
        }

        return this.getSemanticGroupConverter()
            .areTokenListsEqual(compare_value_1.list, compare_value_2.list);
    }

    private isLocationList(compareValue: any): compareValue is ILocationCompareValueList {
        return Boolean(compareValue)
            && typeof compareValue === 'object'
            && (compareValue as ILocationCompareValueList)._ver === 2;
    }

    /** Builds the semantic group converter of the validated field once, from its preset metadata. */
    private getSemanticGroupConverter(): LocationSemanticGroupConverter {
        if (!this.semanticGroupConverter) {
            this.semanticGroupConverter = new LocationSemanticGroupConverter(
                this.conditionPreset[this.condition.field as string]
            );
        }

        return this.semanticGroupConverter;
    }
}